import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { PublicRestService, StatusSkupinySablonE } from '@generated/be';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { statusSkupinySablonOptions } from '@reza-ais-fe/reza-ais-fe-utils';
import { AutocompleteComponent, DatepickerRangeComponent, InputComponent, SelectComponent } from '@shared/ui/forms';
import { Logger, LoggerService, publicApiArgs, SkipLoading } from '@shared/utils';
import { AutoCompleteCompleteEvent } from '@openng/optimus-ui/autocomplete';
import { catchError, of, Subject, switchMap } from 'rxjs';
import { KatalogSkupinSablonFilterFormService } from '../../../services/katalog-skupin-sablon-filter-form.service';
import { HttpContext } from '@angular/common/http';

@Component({
  selector: 'reza-katalog-skupin-sablon-filter',
  imports: [InputComponent, DatepickerRangeComponent, ReactiveFormsModule, AutocompleteComponent, SelectComponent],
  templateUrl: './katalog-skupin-sablon-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class KatalogSkupinSablonFilterComponent {
  readonly filterForm = inject(KatalogSkupinSablonFilterFormService).filterForm;
  readonly publicRestService = inject(PublicRestService);
  readonly loggerService = inject(LoggerService);
  private logger: Logger = this.loggerService.createLogger('KatalogSkupinSablonFilterComponent');

  triggerSearchKodSkupinySablon$ = new Subject<string>();
  triggerSearchKodSablony$ = new Subject<string>();

  kodSkupinySablonResults = signal<string[]>([]);
  kodSablonyResults = signal<string[]>([]);

  statusOptions = [
    {
      id: StatusSkupinySablonE.Aktivni,
      label: statusSkupinySablonOptions[StatusSkupinySablonE.Aktivni].label,
    },
    {
      id: StatusSkupinySablonE.Neaktivni,
      label: statusSkupinySablonOptions[StatusSkupinySablonE.Neaktivni].label,
    },
  ];

  constructor() {
    // Nezobrazování loadingu pro určitě operace (nyní jen na našeptávačích)
    const context = new HttpContext().set(SkipLoading, true);

    this.triggerSearchKodSkupinySablon$
      .asObservable()
      .pipe(
        takeUntilDestroyed(),
        switchMap((query) => {
          return this.publicRestService
            .naseptejKodPublicSkupinySablon({ textInputDto: { text: query } }, ...publicApiArgs(context))
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((result: any) => {
        this.kodSkupinySablonResults.set(result.seznam ?? []);
      });

    this.triggerSearchKodSablony$
      .asObservable()
      .pipe(
        takeUntilDestroyed(),
        switchMap((query) => {
          return this.publicRestService
            .naseptejKodPublicSablony({ textInputDto: { text: query }, ...publicApiArgs() }, ...publicApiArgs(context))
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((result: any) => {
        this.kodSablonyResults.set(result?.seznam ?? []);
      });
  }

  /**
   * Kód skupiny šablon autocomplete search
   */
  handleKodSkupinySablonSearch(event: AutoCompleteCompleteEvent) {
    this.logger.debug('', event);
    if (this.filterForm.controls.kodSkupinySablon.invalid) {
      return;
    }

    this.triggerSearchKodSkupinySablon$.next(event.query);
  }

  /**
   * Kód šablony autocomplete search
   */
  handleKodSablonySearch(event: AutoCompleteCompleteEvent) {
    this.logger.debug('', event);
    if (this.filterForm.controls.kodSablony.invalid) {
      return;
    }

    this.triggerSearchKodSablony$.next(event.query);
  }

  get tooltipKodSkupinySablon(): string {
    return 'Vyhledají se pouze skupiny šablon, které odpovídají zadaným znakům kódu skupiny šablony.  Kód skupiny šablon je přidělen při jejím vytvoření.';
  }

  get tooltipNazevSkupinySablon(): string {
    return 'Vyhledají se pouze skupiny šablon, které odpovídají názvu definovanému uživatelem.';
  }

  get tooltipKodSablony(): string {
    return 'Vyhledají se skupiny šablon, které obsahují zadaný konkrétní kód šablony.';
  }

  get tooltipNazevSablony(): string {
    return 'Vyhledají se skupiny šablon, které obsahují zadaný konkrétní název šablony.';
  }

  get tooltipDatumVytvoreni(): string {
    return 'Jedná se o pole, kam je možné zadat interval nebo konkrétní datum a vyhledají se všechny záznamy, které splňují zadané podmínky.';
  }

  get tooltipDatumUkonceni(): string {
    return 'Jedná se o pole, kam je možné zadat interval nebo konkrétní datum, kdy došlo k ukončení skupiny šablon, a vyhledají se všechny záznamy, které splňují zadané podmínky.';
  }

  get tooltipDatumPosledniZmeny(): string {
    return 'Jedná se o pole, kam je možné zadat interval nebo konkrétní datum, kdy došlo poslední změně, a vyhledají se všechny záznamy, které splňují zadané podmínky.';
  }

  get tooltipStatus(): string {
    return 'Vyfiltruje záznamy, podle statusu.';
  }
}
